import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, FileClock, Filter } from 'lucide-react';
import { api } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Pill, type TomPill } from '../componentes/ui/Pill.js';

interface Registro {
  id: string;
  ts: string;
  usuarioEmail: string | null;
  acao: string;
  alvo: string;
  antes: unknown;
  depois: unknown;
  origemIp: string | null;
  origem: string;
}

interface Pagina {
  registros: Registro[];
  proximoCursor: string | null;
}

/** Traduz o código da ação para algo legível, sem esconder o código. */
const ACOES: Record<string, { texto: string; tom: TomPill }> = {
  'ptr.definir': { texto: 'alterou o reverso de', tom: 'info' },
  'ptr.remover': { texto: 'removeu o reverso de', tom: 'aviso' },
  'ptr.massa': { texto: 'aplicou máscara em massa em', tom: 'info' },
  'bloco.criar': { texto: 'cadastrou o bloco', tom: 'ok' },
  'operadora.criar': { texto: 'cadastrou a operadora', tom: 'ok' },
  'usuario.criar': { texto: 'criou o usuário', tom: 'ok' },
  'usuario.papeis': { texto: 'alterou os papéis de', tom: 'aviso' },
  'usuario.ativar': { texto: 'reativou', tom: 'ok' },
  'usuario.desativar': { texto: 'desativou', tom: 'erro' },
  'usuario.redefinir-senha': { texto: 'redefiniu a senha de', tom: 'aviso' },
  'perfil.atualizar': { texto: 'atualizou o próprio perfil', tom: 'neutro' },
  'perfil.trocar-email': { texto: 'trocou o próprio e-mail', tom: 'aviso' },
  'perfil.2fa-ativar': { texto: 'ativou 2FA em', tom: 'ok' },
  'perfil.2fa-desativar': { texto: 'desativou 2FA em', tom: 'erro' },
  'perfil.encerrar-sessoes': { texto: 'encerrou as sessões de', tom: 'neutro' },
  'no.provisionar': { texto: 'provisionou o nó', tom: 'info' },
  'no.remover': { texto: 'removeu o nó', tom: 'erro' },
};

export function Logs() {
  const [filtros, setFiltros] = useState({ acao: '', usuario: '', alvo: '' });
  const [expandido, setExpandido] = useState<string | null>(null);

  const { data: acoes = [] } = useQuery({
    queryKey: ['auditoria', 'acoes'],
    queryFn: () => api.get<string[]>('/auditoria/acoes'),
  });

  const consulta = useInfiniteQuery({
    queryKey: ['auditoria', filtros],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limite: '50' });
      if (pageParam) p.set('cursor', String(pageParam));
      if (filtros.acao) p.set('acao', filtros.acao);
      if (filtros.usuario) p.set('usuario', filtros.usuario);
      if (filtros.alvo) p.set('alvo', filtros.alvo);
      return api.get<Pagina>(`/auditoria?${p.toString()}`);
    },
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });

  const registros = consulta.data?.pages.flatMap((p) => p.registros) ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-card border border-borda bg-superficie p-3">
        <Filter size={15} className="ml-1 shrink-0 text-texto-mudo" />
        <select
          value={filtros.acao}
          onChange={(e) => setFiltros((f) => ({ ...f, acao: e.target.value }))}
          className="h-9 rounded-chip border border-borda px-2 text-[13px] focus:border-marca focus:outline-none"
        >
          <option value="">Todas as ações</option>
          {acoes.map((a) => (
            <option key={a} value={a}>
              {ACOES[a]?.texto ?? a}
            </option>
          ))}
        </select>
        <input
          value={filtros.usuario}
          onChange={(e) => setFiltros((f) => ({ ...f, usuario: e.target.value }))}
          placeholder="usuário"
          className="h-9 w-40 rounded-chip border border-borda px-2 font-mono text-[12.5px] placeholder:font-sans focus:border-marca focus:outline-none"
        />
        <input
          value={filtros.alvo}
          onChange={(e) => setFiltros((f) => ({ ...f, alvo: e.target.value }))}
          placeholder="alvo (IP, bloco…)"
          className="h-9 w-44 rounded-chip border border-borda px-2 font-mono text-[12.5px] placeholder:font-sans focus:border-marca focus:outline-none"
        />
        {(filtros.acao || filtros.usuario || filtros.alvo) && (
          <button
            onClick={() => setFiltros({ acao: '', usuario: '', alvo: '' })}
            className="text-[12.5px] font-semibold text-marca hover:underline"
          >
            limpar
          </button>
        )}
        <span className="ml-auto mr-1 text-[12px] text-texto-suave">
          {registros.length} {registros.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {consulta.isLoading ? (
        <Esqueleto />
      ) : registros.length === 0 ? (
        <div className="rounded-card border border-borda bg-superficie px-6 py-14 text-center">
          <FileClock size={24} className="mx-auto mb-2 text-texto-mudo" />
          <p className="text-[14px] font-bold">Nenhum registro encontrado</p>
          <p className="mt-1 text-[13px] text-texto-suave">
            A trilha registra toda alteração feita pelo painel, pelo portal ou pela API.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-borda bg-superficie">
          <ul className="divide-y divide-borda-suave">
            {registros.map((r) => {
              const info = ACOES[r.acao];
              const aberto = expandido === r.id;
              const temDetalhe = !!(r.antes || r.depois);

              return (
                <li key={r.id}>
                  <button
                    onClick={() => setExpandido(aberto ? null : r.id)}
                    disabled={!temDetalhe}
                    className={`flex w-full items-start gap-3 px-[18px] py-3 text-left ${
                      temDetalhe ? 'hover:bg-borda-tenue' : 'cursor-default'
                    }`}
                  >
                    <span className="mt-0.5 w-4 shrink-0 text-texto-mudo">
                      {temDetalhe && (aberto ? <ChevronDown size={15} /> : <ChevronRight size={15} />)}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-baseline gap-x-1.5 text-[13.5px]">
                        <strong className="font-bold">{r.usuarioEmail ?? 'Sistema'}</strong>
                        <span className="text-texto-suave">{info?.texto ?? r.acao}</span>
                        <span className="font-mono text-[13px] font-semibold">{r.alvo}</span>
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-2 text-[11.5px] text-texto-mudo">
                        <span>{new Date(r.ts).toLocaleString('pt-BR')}</span>
                        {r.origemIp && <span className="font-mono">{r.origemIp}</span>}
                        <Pill tom="neutro">{r.origem.toLowerCase()}</Pill>
                      </span>
                    </span>

                    {info && <Pill tom={info.tom}>{r.acao}</Pill>}
                  </button>

                  {aberto && temDetalhe && (
                    <div className="grid gap-3 border-t border-borda-suave bg-borda-tenue px-[18px] py-3 sm:grid-cols-2">
                      <Detalhe titulo="Antes" valor={r.antes} />
                      <Detalhe titulo="Depois" valor={r.depois} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {consulta.hasNextPage && (
        <div className="flex justify-center">
          <Botao
            onClick={() => void consulta.fetchNextPage()}
            carregando={consulta.isFetchingNextPage}
          >
            Carregar mais
          </Botao>
        </div>
      )}
    </div>
  );
}

function Detalhe({ titulo, valor }: { titulo: string; valor: unknown }) {
  return (
    <div>
      <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
        {titulo}
      </p>
      {valor ? (
        <pre className="overflow-x-auto rounded-chip bg-superficie p-2.5 font-mono text-[11.5px] leading-relaxed">
          {JSON.stringify(valor, null, 2)}
        </pre>
      ) : (
        <p className="text-[12.5px] italic text-texto-mudo">—</p>
      )}
    </div>
  );
}

function Esqueleto() {
  return (
    <div className="rounded-card border border-borda bg-superficie p-4">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="h-14 border-b border-borda-suave last:border-0">
          <div className="h-3 w-1/3 translate-y-5 animate-pulse rounded bg-borda" />
        </div>
      ))}
    </div>
  );
}
